import { useEffect, useState } from 'react';
import {
  FAQ_ITEMS,
  GUIDES,
  OS_META,
  PROBLEMS,
  getGuide,
  getProblem,
  guidesForOs,
  type DeviceKind,
  type GuideId,
  type OsId,
} from '../data/helpCenter';
import {
  IconBook,
  IconCheck,
  IconChevron,
  IconDevice,
  IconDoc,
  IconHelp,
  IconKey,
  IconLaptop,
  IconShield,
  IconTelegram,
} from './icons';

type HelpScreen =
  | { name: 'hub' }
  | { name: 'device'; device: DeviceKind }
  | { name: 'os'; os: OsId }
  | { name: 'guide'; id: GuideId; step: number }
  | { name: 'problems' }
  | { name: 'problem'; id: string }
  | { name: 'faq' };

export type HelpEntryScreen =
  | { name: 'hub' }
  | { name: 'device'; device: DeviceKind }
  | { name: 'os'; os: OsId };

const HELP_SCREEN_KEY = 'yp_portal_help_screen';

function readHelpScreen(): HelpScreen {
  try {
    const raw = sessionStorage.getItem(HELP_SCREEN_KEY);
    if (!raw) return { name: 'hub' };
    const parsed = JSON.parse(raw) as HelpScreen;
    if (!parsed || typeof parsed !== 'object' || !('name' in parsed)) return { name: 'hub' };
    return parsed;
  } catch {
    return { name: 'hub' };
  }
}

function writeHelpScreen(screen: HelpScreen) {
  try {
    sessionStorage.setItem(HELP_SCREEN_KEY, JSON.stringify(screen));
  } catch {
    /* ignore */
  }
}

/** Prefetch help screen before navigating to the Help tab (HelpCenter reads on mount). */
export function prepareHelpScreen(screen: HelpEntryScreen) {
  writeHelpScreen(screen);
}

interface HelpCenterProps {
  onGoToKeys: () => void;
  onOpenSupport?: () => void;
  botUrl?: string;
  active?: boolean;
}

function BackButton({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" className="help-back" onClick={onClick}>
      <IconChevron size={16} className="help-back-chevron" />
      Назад
    </button>
  );
}

export function HelpCenter({ onGoToKeys, onOpenSupport, botUrl, active = true }: HelpCenterProps) {
  const [screen, setScreenState] = useState<HelpScreen>(() => readHelpScreen());

  const setScreen = (next: HelpScreen) => {
    setScreenState(next);
    writeHelpScreen(next);
  };

  useEffect(() => {
    if (active) window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [active, screen]);

  const goHub = () => setScreen({ name: 'hub' });

  if (screen.name === 'hub') {
    return (
      <div className="help-center">
        <div className="help-intro card">
          <h2 className="help-intro-title">Как настроить доступ</h2>
          <p className="muted help-intro-text">
            Выберите устройство — покажем короткие шаги. Без длинных статей.
          </p>
        </div>

        <div className="help-hub-grid">
          <button
            type="button"
            className="help-hub-card"
            onClick={() => setScreen({ name: 'device', device: 'phone' })}
          >
            <span className="help-hub-icon blue">
              <IconDevice size={20} />
            </span>
            <span className="help-hub-body">
              <strong>Телефон</strong>
              <span className="muted">Android и iPhone</span>
            </span>
            <IconChevron size={18} className="help-hub-arrow" />
          </button>
          <button
            type="button"
            className="help-hub-card"
            onClick={() => setScreen({ name: 'device', device: 'computer' })}
          >
            <span className="help-hub-icon purple">
              <IconLaptop size={20} />
            </span>
            <span className="help-hub-body">
              <strong>Компьютер</strong>
              <span className="muted">Windows и macOS</span>
            </span>
            <IconChevron size={18} className="help-hub-arrow" />
          </button>
          <button type="button" className="help-hub-card" onClick={() => setScreen({ name: 'problems' })}>
            <span className="help-hub-icon orange">
              <IconHelp size={20} />
            </span>
            <span className="help-hub-body">
              <strong>Решение проблем</strong>
              <span className="muted">Частые ошибки и что делать</span>
            </span>
            <IconChevron size={18} className="help-hub-arrow" />
          </button>
          <button type="button" className="help-hub-card" onClick={() => setScreen({ name: 'faq' })}>
            <span className="help-hub-icon green">
              <IconBook size={20} />
            </span>
            <span className="help-hub-body">
              <strong>FAQ</strong>
              <span className="muted">Ответы на популярные вопросы</span>
            </span>
            <IconChevron size={18} className="help-hub-arrow" />
          </button>
        </div>

        <div className="help-side-actions">
          <button type="button" className="btn btn-primary" onClick={onGoToKeys}>
            <IconKey size={16} />
            Перейти к ключам
          </button>
          {onOpenSupport ? (
            <button type="button" className="btn btn-ghost" onClick={onOpenSupport}>
              <IconHelp size={16} />
              Написать в поддержку
            </button>
          ) : botUrl ? (
            <a href={botUrl} target="_blank" rel="noreferrer" className="btn btn-ghost">
              <IconTelegram size={16} />
              Написать в бота
            </a>
          ) : null}
        </div>
      </div>
    );
  }

  if (screen.name === 'device') {
    const oss = (Object.keys(OS_META) as OsId[]).filter((os) => OS_META[os].device === screen.device);
    return (
      <div className="help-center content-enter">
        <BackButton onClick={goHub} />
        <h2 className="help-section-title">
          {screen.device === 'phone' ? 'Телефон' : 'Компьютер'}
        </h2>
        <p className="muted help-section-sub">Выберите систему</p>
        <div className="help-hub-grid">
          {oss.map((os) => (
            <button
              key={os}
              type="button"
              className="help-hub-card"
              onClick={() => setScreen({ name: 'os', os })}
            >
              <span className="help-hub-body">
                <strong>{OS_META[os].label}</strong>
                <span className="muted">{OS_META[os].blurb}</span>
              </span>
              <IconChevron size={18} className="help-hub-arrow" />
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (screen.name === 'os') {
    const guides = guidesForOs(screen.os);
    return (
      <div className="help-center content-enter">
        <BackButton onClick={() => setScreen({ name: 'device', device: OS_META[screen.os].device })} />
        <h2 className="help-section-title">{OS_META[screen.os].label}</h2>
        <p className="muted help-section-sub">Какой протокол настраиваете?</p>
        <div className="help-hub-grid">
          {guides.map((g) => (
            <button
              key={g.id}
              type="button"
              className="help-hub-card"
              onClick={() => setScreen({ name: 'guide', id: g.id, step: -1 })}
            >
              <span className={`help-proto-badge ${g.protocol}`}>
                {g.protocol === 'wg' ? 'WG' : 'OVPN'}
              </span>
              <span className="help-hub-body">
                <strong>{g.protocol === 'wg' ? 'WireGuard' : 'OpenVPN'}</strong>
                <span className="muted">~ {g.duration}</span>
              </span>
              <IconChevron size={18} className="help-hub-arrow" />
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (screen.name === 'guide') {
    const guide = getGuide(screen.id);
    if (!guide) {
      return (
        <div className="help-center">
          <BackButton onClick={goHub} />
          <p className="error-text">Инструкция не найдена.</p>
        </div>
      );
    }

    if (screen.step < 0) {
      return (
        <div className="help-center content-enter">
          <BackButton onClick={() => setScreen({ name: 'os', os: guide.os })} />
          <div className="card help-guide-intro">
            <div className="help-guide-kicker">
              <IconShield size={18} />
              {guide.title}
            </div>
            <h2 className="help-section-title" style={{ marginBottom: '0.35rem' }}>
              Настройка займёт {guide.duration}
            </h2>
            <p className="muted" style={{ marginTop: 0 }}>
              Что понадобится:
            </p>
            <ul className="help-need-list">
              {guide.needs.map((n) => (
                <li key={n}>{n}</li>
              ))}
            </ul>
            <div className="help-step-actions">
              <button
                type="button"
                className="btn btn-primary btn-block"
                onClick={() => setScreen({ name: 'guide', id: guide.id, step: 0 })}
              >
                Начать
              </button>
              <button type="button" className="btn btn-ghost btn-block" onClick={onGoToKeys}>
                Сначала скачать ключ
              </button>
            </div>
          </div>
        </div>
      );
    }

    const total = guide.steps.length;
    const stepIndex = Math.min(Math.max(screen.step, 0), total - 1);
    const step = guide.steps[stepIndex];
    const progress = ((stepIndex + 1) / total) * 100;
    const isLast = stepIndex >= total - 1;

    return (
      <div className="help-center content-enter">
        <BackButton
          onClick={() =>
            setScreen(
              stepIndex === 0
                ? { name: 'guide', id: guide.id, step: -1 }
                : { name: 'guide', id: guide.id, step: stepIndex - 1 },
            )
          }
        />

        <div className="help-progress-meta">
          <span className="muted">
            Шаг {stepIndex + 1} из {total}
          </span>
          <span className="help-progress-title">{guide.title}</span>
        </div>
        <div className="help-progress-track" aria-hidden>
          <div className="help-progress-fill" style={{ width: `${progress}%` }} />
        </div>

        <div className="card help-step-card">
          <h3 className="help-step-title">{step.title}</h3>
          <ul className="help-step-list">
            {step.body.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
          {step.tip && <div className="help-tip">{step.tip}</div>}
          {step.link && (
            <a
              href={step.link.href}
              target="_blank"
              rel="noreferrer"
              className="btn btn-primary"
              style={{ marginTop: '0.85rem' }}
            >
              {step.link.label}
            </a>
          )}
        </div>

        {isLast && (
          <div className="card help-success-card">
            <div className="help-success-icon">
              <IconCheck size={22} />
            </div>
            <strong>Готово</strong>
            <p className="muted" style={{ margin: '0.35rem 0 0' }}>
              Если профиль включён и сайты открываются — настройка успешна.
            </p>
          </div>
        )}

        <div className="help-step-nav">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() =>
              setScreen(
                stepIndex === 0
                  ? { name: 'guide', id: guide.id, step: -1 }
                  : { name: 'guide', id: guide.id, step: stepIndex - 1 },
              )
            }
          >
            Назад
          </button>
          {!isLast ? (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setScreen({ name: 'guide', id: guide.id, step: stepIndex + 1 })}
            >
              {step.actionLabel ?? 'Далее'}
            </button>
          ) : (
            <button type="button" className="btn btn-primary" onClick={goHub}>
              К инструкциям
            </button>
          )}
        </div>

        <div className="help-step-footer">
          <button type="button" className="btn btn-ghost btn-block" onClick={onGoToKeys}>
            <IconKey size={16} />
            Открыть ключи
          </button>
          <button
            type="button"
            className="help-trouble-link"
            onClick={() => setScreen({ name: 'problems' })}
          >
            Не получилось? Смотрите решение проблем
          </button>
        </div>
      </div>
    );
  }

  if (screen.name === 'problems') {
    return (
      <div className="help-center content-enter">
        <BackButton onClick={goHub} />
        <h2 className="help-section-title">Решение проблем</h2>
        <p className="muted help-section-sub">Выберите ситуацию — покажем короткий план действий</p>
        <div className="help-list">
          {PROBLEMS.map((p) => (
            <button
              key={p.id}
              type="button"
              className="help-list-item"
              onClick={() => setScreen({ name: 'problem', id: p.id })}
            >
              <span className="help-list-item-body">
                <strong>{p.title}</strong>
                <span className="muted">{p.summary}</span>
              </span>
              <IconChevron size={18} className="help-hub-arrow" />
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (screen.name === 'problem') {
    const article = getProblem(screen.id);
    if (!article) {
      return (
        <div className="help-center">
          <BackButton onClick={() => setScreen({ name: 'problems' })} />
          <p className="error-text">Статья не найдена.</p>
        </div>
      );
    }
    return (
      <div className="help-center content-enter">
        <BackButton onClick={() => setScreen({ name: 'problems' })} />
        <div className="card help-article">
          <h2 className="help-section-title" style={{ marginTop: 0 }}>
            {article.title}
          </h2>
          <p className="muted">{article.summary}</p>
          {article.causes.length > 0 && (
            <>
              <h3 className="help-article-h">Возможные причины</h3>
              <ul className="help-step-list">
                {article.causes.map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </ul>
            </>
          )}
          <h3 className="help-article-h">Что сделать</h3>
          <ol className="help-step-list help-step-list-numbered">
            {article.steps.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ol>
        </div>
        <div className="help-side-actions">
          <button type="button" className="btn btn-primary" onClick={onGoToKeys}>
            Перейти к ключам
          </button>
          {onOpenSupport ? (
            <button type="button" className="btn btn-ghost" onClick={onOpenSupport}>
              <IconHelp size={16} />
              Написать в поддержку
            </button>
          ) : botUrl ? (
            <a href={botUrl} target="_blank" rel="noreferrer" className="btn btn-ghost">
              <IconTelegram size={16} />
              Написать в бота
            </a>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="help-center content-enter">
      <BackButton onClick={goHub} />
      <h2 className="help-section-title">FAQ</h2>
      <p className="muted help-section-sub">Короткие ответы без воды</p>
      <div className="help-faq-list">
        {FAQ_ITEMS.map((item) => (
          <details key={item.id} className="help-faq-item">
            <summary>{item.question}</summary>
            <p>{item.answer}</p>
          </details>
        ))}
      </div>
      <div className="help-side-actions" style={{ marginTop: '1.25rem' }}>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => setScreen({ name: 'guide', id: GUIDES[0].id, step: -1 })}
        >
          <IconDoc size={16} />
          Открыть инструкцию
        </button>
      </div>
    </div>
  );
}
